import { useEffect, useMemo } from 'react';
import { ArrowRight, Bell, CheckCircle2, ClipboardList } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useAuthStore } from '@/stores/auth';
import { useOnboardingStore } from '@/stores/onboarding';
import { useNotificationStore } from '@/stores/notifications';
import { cn } from '@/lib/utils';

function openOnboardingModal() {
  window.dispatchEvent(new CustomEvent('leftclick:onboarding-open-modal'));
}

function navigateHash(path: string) {
  window.location.hash = path.startsWith('/') ? path : `/${path}`;
}

export function GuidanceHubCard({ className }: { className?: string }) {
  const user = useAuthStore((s) => s.user);
  const payload = useOnboardingStore((s) => s.payload);
  const loading = useOnboardingStore((s) => s.loading);
  const fetchOnboarding = useOnboardingStore((s) => s.fetch);
  const resetOnboarding = useOnboardingStore((s) => s.reset);
  const unreadCount = useNotificationStore((s) => s.unreadCount);
  const fetchUnreadCount = useNotificationStore((s) => s.fetchUnreadCount);

  useEffect(() => {
    if (!user?.id) {
      resetOnboarding();
      return;
    }
    void fetchOnboarding();
  }, [user?.id, fetchOnboarding, resetOnboarding]);

  useEffect(() => {
    void fetchUnreadCount();
  }, [fetchUnreadCount, user?.id]);

  const completion = useMemo(() => {
    const tasks = payload?.tasks ?? [];
    const completed = tasks.filter((t) => Boolean(t.progress?.completed_at)).length;
    return { completed, total: tasks.length };
  }, [payload]);

  const nextTasks = useMemo(() => {
    const tasks = payload?.tasks ?? [];
    return tasks.filter((t) => !t.progress?.completed_at).slice(0, 4);
  }, [payload]);

  const progressPct =
    completion.total > 0 ? Math.round((completion.completed / completion.total) * 100) : 0;

  if (!user) return null;

  if (!loading && !payload) {
    if (unreadCount <= 0) return null;
    return (
      <Card className={cn('w-full border-border', className)}>
        <CardHeader className="pb-2">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                <Bell className="h-5 w-5" aria-hidden />
              </span>
              <div>
                <CardTitle className="text-lg">Benachrichtigungen</CardTitle>
                <CardDescription className="mt-1 max-w-xl">
                  Sie haben ungelesene Hinweise — öffnen Sie die Übersicht, um nichts zu verpassen.
                </CardDescription>
              </div>
            </div>
            <Button type="button" size="sm" className="gap-1.5 sm:shrink-0" onClick={() => navigateHash('/notifications')}>
              Zur Übersicht
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
      </Card>
    );
  }

  if (loading && !payload) {
    return (
      <Card className={cn('w-full overflow-hidden', className)}>
        <div className="h-28 animate-pulse bg-card-hover" />
      </Card>
    );
  }

  if (!payload) return null;

  const allDone = completion.total > 0 && completion.completed >= completion.total;

  return (
    <Card className={cn('w-full border-border', className)}>
      <CardHeader className="pb-2">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 flex-1 space-y-3">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                <ClipboardList className="h-5 w-5" aria-hidden />
              </span>
              <div className="min-w-0">
                <CardTitle className="text-lg leading-tight">{payload.template.name}</CardTitle>
                <CardDescription className="mt-1 max-w-2xl">
                  {allDone
                    ? 'Sie haben alle Schritte dieses Programms erledigt — hier können Sie die Liste noch einsehen oder die Benachrichtigungen prüfen.'
                    : 'So sehen Sie auf einen Blick, was als Nächstes ansteht und wie weit Sie schon sind.'}
                </CardDescription>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <span className="font-medium text-muted dark:text-foreground">
                  Fortschritt: {completion.completed} von {completion.total}
                </span>
                <span className="tabular-nums text-muted">{progressPct}%</span>
              </div>
              <Progress value={progressPct} className="h-2.5" />
            </div>
          </div>

          <div className="flex w-full shrink-0 flex-col gap-2 sm:flex-row lg:w-auto lg:flex-col lg:items-end">
            <Button type="button" size="sm" className="gap-1.5 sm:w-auto" onClick={() => openOnboardingModal()}>
              Checkliste im Dialog
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => navigateHash('/onboarding')}>
              Zur Onboarding-Seite
            </Button>
            {unreadCount > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => navigateHash('/notifications')}
              >
                <Bell className="h-4 w-4" />
                {unreadCount} Benachrichtigung{unreadCount === 1 ? '' : 'en'}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {allDone ? (
          <div className="flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground dark:bg-card">
            <CheckCircle2 className="h-5 w-5 shrink-0 text-muted" aria-hidden />
            <span>Alle Punkte abgeschlossen — super!</span>
          </div>
        ) : (
          <ul className="grid gap-2 sm:grid-cols-2">
            {nextTasks.map((task) => (
              <li
                key={task.id}
                className="flex min-h-[4.5rem] flex-col justify-between rounded-lg border border-border bg-background p-3 text-sm dark:bg-card"
              >
                <span className="font-medium leading-snug text-foreground">{task.title}</span>
                {task.description && (
                  <span className="mt-1 line-clamp-2 text-xs text-muted">{task.description}</span>
                )}
                {task.action_path && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="mt-2 h-8 justify-start px-0 text-foreground dark:hover:text-white"
                    onClick={() => navigateHash(task.action_path!)}
                  >
                    Öffnen <ArrowRight className="ml-1 h-3.5 w-3.5" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
